<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="
      referencesPendingFullTextReviewConnection
    "
    :pending-connection-query="
      $apollo.queries.referencesPendingFullTextReviewConnection
    "
    :history-connection="referencesByVoteHistoryConnection"
    :history-connection-query="
      $apollo.queries.referencesByVoteHistoryConnection
    "
    :search-input.sync="searchInput"
    :revoke-vote-func="revokeVote"
    :get-vote-by-current-user-func="getVoteByCurrentUser"
    :filtering-input="filteringInput"
    restrict-to-voting-only
    full-text-review
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <page-content-placeholder
        icon="check"
        icon-color="success"
        prompt-text="Complete"
      >
        <template #message>
          All pending references have been reviewed.
        </template>
      </page-content-placeholder>
    </template>
  </screening-workflow-panel>
</template>

<script>
import PageContentPlaceholder from '@/components/Common/PageContentPlaceholder';
import ScreeningWorkflowPanel from '@/components/ScreeningPageV2/ScreenTab/ScreeningWorkflowPanel';
import {
  GetReferencesPendingFullTextReview,
  GetReferencesByFullTextReviewVoteHistory,
} from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;

export default {
  name: 'ReviewTabPending',
  components: {
    ScreeningWorkflowPanel,
    PageContentPlaceholder,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    referencesPendingFullTextReviewConnection: null,
    referencesByVoteHistoryConnection: null,
  }),
  apollo: {
    referencesPendingFullTextReviewConnection: {
      query: GetReferencesPendingFullTextReview,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesPendingFullTextReview
        ),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    referencesByVoteHistoryConnection: {
      query: GetReferencesByFullTextReviewVoteHistory,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesByFullTextReviewVoteHistory
        ),
      skip() {
        return (
          !this.review?.id ||
          !this.referencesPendingFullTextReviewConnection
        );
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (active && this.referencesPendingFullTextReviewConnection) {
        this.$apollo.queries.referencesPendingFullTextReviewConnection.refetch();
        this.$apollo.queries.referencesByVoteHistoryConnection.refetch();
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesPendingFullTextReviewConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      revokeVote: 'fullTextReviewVote/deleteWithCacheUpdate',
    }),
    getVoteByCurrentUser(reference) {
      return reference?.getFullTextReviewVoteBy(
        this.currentResearcher
      );
    },
  },
};
</script>
